import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DuplicateAlertModal from "@/components/feature/DuplicateAlertModal";
import { useNotifications } from "@/hooks/useNotifications";
import { useNotificationAlerts } from "@/hooks/useNotificationAlerts";

interface NotificationLayerProps {
  /** Chỉ admin / quản lý kho mới cần chuông + popup cảnh báo */
  enabled: boolean;
  /** Hoãn khởi động một nhịp để trang mới mở trước, không tranh đường truyền */
  delayMs?: number;
}

/**
 * Toàn bộ phần thông báo realtime được tách riêng ở đây.
 *
 * Lý do: hook thông báo tự cập nhật mỗi 15 giây và bắt realtime. Nếu đặt trong
 * khung layout, mỗi lần có thông báo là cả khung menu + cả trang bên phải bị vẽ
 * lại => gõ chữ hay bấm menu đều thấy khựng. Tách ra component riêng thì dữ liệu
 * thông báo đổi cũng chỉ ảnh hưởng đúng nó.
 */
export default function NotificationLayer({
  enabled,
  delayMs = 1500,
}: NotificationLayerProps) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setReady(false);
      return;
    }
    // Chờ trang vẽ xong nhịp đầu rồi mới hỏi database thông báo.
    const timer = window.setTimeout(() => setReady(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [enabled, delayMs]);

  const notifications = useNotifications(enabled && ready);
  const alertState = useNotificationAlerts({
    enabled: enabled && ready,
    initialized: notifications.initialized,
    items: notifications.items,
    refresh: notifications.refresh,
  });

  const navigate = useNavigate();

  // Đi tới Báo cáo để xử lý chi tiết: chỉ gỡ popup trong phiên này, KHÔNG đánh
  // dấu đã đọc — rời đi mà chưa xử lý thì popup hiện lại khi tải lại trang.
  const handleOpenReport = useCallback(() => {
    alertState.dismissAll();
    navigate("/dashboard");
  }, [alertState, navigate]);

  if (!enabled) return null;

  return (
    <DuplicateAlertModal
      alerts={alertState.alerts}
      onOpenReport={handleOpenReport}
      onClose={alertState.dismissAll}
    />
  );
}